import PlusIcon from "@mui/icons-material/Add";
import { 
  Box, 
  Button, 
  Container,
  Grid, 
  Pagination, 
  Stack, 
  SvgIcon, 
  Typography,
  Avatar,
  useTheme,
  alpha
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../common/components/loader";
import DashboardLayout from "../../common/layouts/dashboard";
import { useAppDispatch } from "../../core/hooks/useStore";
import { RootState } from "../../core/store/store";
import { useDialog } from "../../hooks/use-dialog";
import { CreateIntegrationDialog } from "./create-integration-dialog";
import IntegrationsCard from "./integrations-card";
import IntegrationsEmpty from "./integrations-empty";
import integrationsSlice from "./integrations.slice";

const Integrations = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { apiUrl } = useSelector((state: RootState) => state.home);
  const integrationState = useSelector((state: RootState) => state.integrations);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const createDialog = useDialog();
  const page = integrationState.page;

  useEffect(() => {
    document.title = "Wordform AI - Integrations";
  }, []);

  const getIntegrations = async () => {
    try {
      setLoading(true);
      const page = integrationState.page;
      const data = { page, pageSize: 6 };
      const response: any = await axios.post(`${apiUrl}/integration/list`, data);
      const result = response.data.result;
      dispatch(integrationsSlice.actions.setIntegrations(result.integrations));
      dispatch(integrationsSlice.actions.setIntegrationsCount(result.count));
    } catch (e: any) {
      if (e.response?.data?.error?.message) {
        toast.error("Error: " + e.response.data.error.message);
      } else if (e.request) {
        toast.dismiss();
        toast.error("Network error occurred...");
      } else {
        toast.error("Error listing integrations...");
      }
    }
    setLoading(false);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    dispatch(integrationsSlice.actions.setPage(value));
  };

  useEffect(() => {
    getIntegrations();
  }, [page]);


  return (
    <DashboardLayout active={"integrations"} name="Integrations">
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
                Integrations
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Connect your WordPress sites and publish content
              </Typography>
            </Box>
            <Button
              onClick={createDialog.handleOpen}
              size="large"
              startIcon={<PlusIcon />}
              variant="contained"
              sx={{ 
                px: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                }
              }}
            >
              Add Integration
            </Button>
          </Stack>
        </Box>

        {/* Main Content */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Loader />
          </Box>
        )}
        
        {!loading && integrationState.integrations.length === 0 && (
          <IntegrationsEmpty dialog={createDialog} />
        )}
        
        {!loading && integrationState.integrations.length > 0 && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Your Connected Sites
              </Typography>
              <Grid container spacing={3}>
                {integrationState.integrations.map((integration, k) => {
                  return (
                    <IntegrationsCard
                      key={integration.id}
                      id={integration.id}
                      domain={integration.data.domain as string}
                    />
                  );
                })}
              </Grid>
            </Box>
            
            {Math.ceil(integrationState.integrationsCount / 6) > 1 && (
              <Stack sx={{ my: 4 }} justifyContent="center" alignItems="center">
                <Pagination
                  count={Math.ceil(integrationState.integrationsCount / 6)}
                  variant="outlined"
                  shape="rounded"
                  size="large"
                  onChange={handlePageChange}
                  page={page}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        borderColor: theme.palette.primary.main,
                        '&:hover': {
                          bgcolor: theme.palette.primary.dark
                        }
                      }
                    }
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </Container>

      <CreateIntegrationDialog onClose={createDialog.handleClose} open={createDialog.open} />
    </DashboardLayout>
  );
};

export default Integrations;